// src/contexts/PointsContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, increment } from '../lib/firestoreClient';
import { db } from '../lib/firebase';
import useAuth from '../hooks/useAuth';
import { getBadgeForPoints } from '../utils/pointsCalculator';
import { errorDev } from '../utils/logger.js';

const PointsContext = createContext({ points: 0, badge: 'Seedling', addPoints: () => {} });

export function PointsProvider({ children }) {
  const { user } = useAuth();
  const [points, setPoints] = useState(0);
  const [badge, setBadge] = useState('Seedling');

  useEffect(() => {
    if (!user) {
      setPoints(0);
      setBadge('Seedling');
      return;
    }

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const userPoints = userData.points || 0;
        setPoints(userPoints);
        setBadge(getBadgeForPoints(userPoints));
      }
    }, () => {
      // Silent fail — non-critical
    });

    return () => unsubscribe();
  }, [user]);

  const addPoints = useCallback(async (amount) => {
    if (!user || !amount) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { points: increment(amount) });
    } catch (err) {
      // Silent fail — non-critical
    errorDev("Operation failed:", err);
    }
  }, [user]);

  return (
    <PointsContext.Provider value={{ points, badge, addPoints }}>
      {children}
    </PointsContext.Provider>
  );
}

export function usePoints() {
  return useContext(PointsContext);
}

export default PointsContext;

